import { type RefObject, useEffect } from 'react';

interface UseMultiSelectKeyboardNavigationProps {
  isOpen: boolean;
  showSearch: boolean;
  searchInputRef: RefObject<HTMLInputElement>;
  filteredOptions: { value: string }[];
  focusedIndex: number;
  setFocusedIndex: (fn: (prev: number) => number) => void;
  setIsOpen: (open: boolean) => void;
  handleToggle: (value: string) => void;
  optionRefs: RefObject<(HTMLButtonElement | null)[]>;
  triggerRef: RefObject<HTMLButtonElement>;
}

export function useMultiSelectKeyboardNavigation({
  isOpen,
  showSearch,
  searchInputRef,
  filteredOptions,
  focusedIndex,
  setFocusedIndex,
  setIsOpen,
  handleToggle,
  optionRefs,
  triggerRef,
}: UseMultiSelectKeyboardNavigationProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (showSearch && document.activeElement === searchInputRef.current) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
        } else if (e.key === 'Tab') {
          setIsOpen(false);
          return;
        } else {
          return;
        }
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = Math.min(prev + 1, filteredOptions.length - 1);
            optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const next = Math.max(prev - 1, 0);
            optionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
            return next;
          });
          break;
        case 'Enter':
        case ' ': {
          if (showSearch && document.activeElement === searchInputRef.current && e.key === ' ') {
            return;
          }
          e.preventDefault();
          const option = filteredOptions[focusedIndex];
          if (option?.value !== undefined) {
            handleToggle(option.value);
          }
          break;
        }
        case 'Tab':
          setIsOpen(false);
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [
    isOpen,
    focusedIndex,
    filteredOptions,
    showSearch,
    handleToggle,
    searchInputRef,
    setFocusedIndex,
    setIsOpen,
    optionRefs,
    triggerRef,
  ]);
}
